import {
  Typography,
  makeStyles,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Highlight,
  useClientOptions,
  IconButton
} from '@paintscout/ui'
import type { DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import type { Contact } from '@paintscout/api'
import { useContactLazyQuery } from '@paintscout/api'
import { getObjectLabels, setContact, setSecondaryContact } from '@paintscout/util/builder'
import { useQuote } from '@paintscout/react-quote'
import type { QuoteDocument } from 'paintscout'
import React, { useCallback, useEffect, useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'

import ContactForm from 'src/components/forms/ContactForm'

const useStyles = makeStyles((_theme: Theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))

export interface QuoteContactDialogProps extends DialogProps {
  isNew?: boolean
  isSecondary?: boolean
  queryString?: string
  onConfirm: (quote: QuoteDocument, shouldSaveContact: boolean) => any
  onCancel?: (event: React.MouseEvent<HTMLElement>) => any
}

function QuoteContactDialog({
  isNew,
  isSecondary,
  queryString = '',
  onCancel,
  onConfirm,
  ...baseDialogProps
}: QuoteContactDialogProps) {
  const classes = useStyles()
  const { quote } = useQuote()

  const { options } = useClientOptions()
  const isCreating = !quote.contact || isNew
  const [newerContact, setNewerContact] = useState<Contact>(null)
  const { id, rev, ...contact } = quote.contact || {}
  const { id: secondaryId, rev: secondaryRev, ...secondaryContact } = quote.secondaryContact || {}
  const { searchFirst, searchLast } = queryString.split(' ').reduce(
    (acc, curr, i) => {
      switch (i) {
        case 0:
          return { ...acc, searchFirst: curr }
        case 1:
          return { ...acc, searchLast: curr }
        default:
          return acc
      }
    },
    { searchFirst: '', searchLast: '' }
  )
  const [values, setValues] = useState<Partial<Contact>>(
    !isNew && !isSecondary && (id || rev)
      ? {
          ...contact,
          _id: id,
          _rev: rev
        }
      : !isNew && (secondaryId || secondaryRev)
        ? {
            ...secondaryContact,
            _id: secondaryId,
            _rev: secondaryRev
          }
        : queryString
          ? {
              first_name: searchFirst,
              last_name: searchLast
            }
          : null
  )

  const objectLabels = getObjectLabels({ options, invoice: !!quote?.is_invoice })

  const [fetchContact, { loading }] = useContactLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: useCallback((data) => {
      const contactRev = data?.contact?._rev
      if (contactRev && values._rev && contactRev !== values._rev) {
        setNewerContact(data.contact as Contact)
      }
    }, []),
    // since we'll have quote.contact, there isn't any reason to notify the user
    onError: useCallback((e) => console.error(e), [])
  })

  useEffect(() => {
    if (!isNew && !isSecondary && quote.contact?.id) {
      fetchContact({ variables: { id: quote.contact.id } })
    }
    if (!isNew && isSecondary && quote.secondaryContact?.id) {
      fetchContact({ variables: { id: quote.secondaryContact.id } })
    }
  }, [])

  return (
    <Dialog open={open} maxWidth="md" data-testid="quote-contact-dialog" {...baseDialogProps}>
      <DialogTitle
        loading={loading}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        <Typography variant="h3" gutterBottom={!!newerContact}>
          {isCreating ? 'Create New' : 'Edit'} Contact
        </Typography>
        {newerContact && (
          <Typography variant="body2">
            This {objectLabels.quote.value} is using an older version of this Contact. Changes will only be saved to
            this {objectLabels.quote.value}.{' '}
            <button
              onClick={() => {
                setNewerContact(null)
                setValues(newerContact)
              }}
            >
              <Highlight fontWeight="medium">Click here to use the latest Contact.</Highlight>
            </button>
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <ContactForm
          id="quote-contact-form"
          data-testid="quote-contact-form"
          contact={values}
          disableCompanySelector={!!newerContact}
          onSubmit={handleSubmit}
          isSecondary={isSecondary}
        />
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          form="quote-contact-form"
          variant={'contained'}
          disabled={loading}
          data-testid="contact-dialog-confirm-button"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit(newContact: Contact) {
    let updatedQuote: QuoteDocument
    if (!isSecondary) {
      updatedQuote = setContact({ contact: newContact, quote })
    } else {
      updatedQuote = setSecondaryContact({ secondaryContact: newContact, quote })
    }

    onConfirm(updatedQuote, !newerContact)
  }
}

export default QuoteContactDialog
